<template>
  <div class="page-wrapper">
    <div class="content container-fluid">
      <!-- Header -->
      <div
        class="page-header d-flex align-items-center justify-content-between"
      >
        <div>
          <h3 class="page-title">Quản lý bảng lương</h3>
          <ul class="breadcrumb mb-0">
            <li class="breadcrumb-item"><a href="/">Dashboard</a></li>
            <li class="breadcrumb-item active">Payroll</li>
          </ul>
        </div>
        <div class="d-flex align-items-center">
          <div class="mr-2">
            <input
              type="month"
              class="form-control"
              v-model="ui.month"
              @change="onMonthChange"
            />
          </div>
          <button
            class="btn btn-outline-secondary"
            @click="refreshAll"
            :disabled="loading.any"
          >
            <i class="fa fa-refresh"></i>
          </button>
        </div>
      </div>

      <div class="row">
        <!-- ===== CỘT TRÁI: Quy trình ===== -->
        <div class="col-xl-5">
          <!-- B1: Tạo bảng lương -->
          <div class="card border-left-primary shadow-soft mb-3">
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between mb-2"
              >
                <h5 class="card-title mb-0">Bước 1 — Tạo bảng lương</h5>
                <span
                  class="badge"
                  :class="bl.id ? 'badge-success' : 'badge-secondary'"
                  >{{ bl.id ? "Đã tạo" : "Chưa tạo" }}</span
                >
              </div>
              <div class="row">
                <div class="col-md-8">
                  <label class="small">Mô tả</label>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="VD: Lương tháng {{ ui.month }}"
                    v-model.trim="form.moTa"
                  />
                </div>
                <div class="col-md-4">
                  <label class="small">&nbsp;</label>
                  <button
                    class="btn btn-primary btn-block"
                    :disabled="!ui.month || loading.taobang"
                    @click="taoBang"
                  >
                    <span v-if="loading.taobang"
                      ><i class="fa fa-spinner fa-spin mr-1"></i>Đang tạo…</span
                    >
                    <span v-else><i class="fa fa-plus mr-1"></i>Tạo bảng</span>
                  </button>
                </div>
              </div>
              <div v-if="bl.id" class="small text-muted mt-2">
                ID: <code>{{ bl.id }}</code> • Trạng thái:
                <b>{{ bl.trangThai || "—" }}</b>
              </div>
            </div>
          </div>

          <!-- B2: Lập danh sách nhân viên -->
          <div
            class="card border-left-info shadow-soft mb-3"
            :class="{ 'bg-disabled': !bl.id }"
          >
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between mb-2"
              >
                <h5 class="card-title mb-0">Bước 2 — Lập nhân viên</h5>
                <span class="badge badge-light">Tuỳ chọn theo phòng ban</span>
              </div>
              <div class="row align-items-end">
                <div class="col-md-8">
                  <label class="small">Phòng ban</label>
                  <select
                    class="form-control"
                    v-model="ui.pbId"
                    :disabled="!bl.id || loading.pbs"
                  >
                    <option :value="null">— Tất cả phòng ban —</option>
                    <option
                      v-for="pb in options.phongBans"
                      :key="pb.idPhongBan || pb.id"
                      :value="pb.idPhongBan || pb.id"
                    >
                      {{ pb.tenPhongBan || pb.ten }}
                    </option>
                  </select>
                </div>
                <div class="col-md-4">
                  <button
                    class="btn btn-info btn-block"
                    :disabled="!bl.id || loading.lapnv"
                    @click="lapNhanVien"
                  >
                    <span v-if="loading.lapnv"
                      ><i class="fa fa-spinner fa-spin mr-1"></i>Đang lập…</span
                    >
                    <span v-else><i class="fa fa-users mr-1"></i>Lập NV</span>
                  </button>
                </div>
              </div>
              <div v-if="last.lapCount != null" class="small text-muted mt-2">
                Đã thêm/refresh: <b>{{ last.lapCount }}</b> nhân viên.
              </div>
            </div>
          </div>

          <!-- B3: Tính toán -->
          <div
            class="card border-left-warning shadow-soft mb-3"
            :class="{ 'bg-disabled': !bl.id }"
          >
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between mb-2"
              >
                <h5 class="card-title mb-0">Bước 3 — Tính toán</h5>
                <span class="badge badge-light">Chạy công thức</span>
              </div>
              <button
                class="btn btn-warning"
                :disabled="!bl.id || loading.tinh"
                @click="tinhToan"
              >
                <span v-if="loading.tinh"
                  ><i class="fa fa-spinner fa-spin mr-1"></i>Đang tính…</span
                >
                <span v-else
                  ><i class="fa fa-calculator mr-1"></i>Tính toàn bộ</span
                >
              </button>
              <span v-if="last.calcCount != null" class="small text-muted ml-2"
                >({{ last.calcCount }} NV)</span
              >
            </div>
          </div>

          <!-- B4: Khoá & Export -->
          <div
            class="card border-left-success shadow-soft"
            :class="{ 'bg-disabled': !bl.id }"
          >
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between mb-2"
              >
                <h5 class="card-title mb-0">Bước 4 — Khoá & Xuất Excel</h5>
                <span
                  class="badge"
                  :class="bl.locked ? 'badge-success' : 'badge-secondary'"
                  >{{ bl.locked ? "ĐÃ KHOÁ" : "CHƯA KHOÁ" }}</span
                >
              </div>
              <div class="d-flex flex-wrap gap-2">
                <button
                  class="btn btn-outline-success mr-2"
                  :disabled="!bl.id || bl.locked || loading.khoa"
                  @click="khoaBang"
                >
                  <span v-if="loading.khoa"
                    ><i class="fa fa-spinner fa-spin mr-1"></i>Đang khoá…</span
                  >
                  <span v-else><i class="fa fa-lock mr-1"></i>Khoá bảng</span>
                </button>
                <button
                  class="btn btn-outline-dark"
                  :disabled="!bl.id || loading.exportAll"
                  @click="exportBangLuong"
                >
                  <span v-if="loading.exportAll"
                    ><i class="fa fa-spinner fa-spin mr-1"></i>Đang xuất…</span
                  >
                  <span v-else
                    ><i class="fa fa-file-excel-o mr-1"></i>Export toàn bộ</span
                  >
                </button>
              </div>
              <div class="small text-muted mt-2" v-if="bl.ngayChot">
                Ngày chốt: {{ bl.ngayChot }}
              </div>
            </div>
          </div>

          <!-- Preview nhanh của TÔI -->
          <div class="card border-left-info shadow-soft mt-3">
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between mb-2"
              >
                <h5 class="card-title mb-0">Tạm tính của tôi</h5>
                <small class="text-muted">{{ ui.month }}</small>
              </div>
              <div v-if="loading.preview" class="text-muted">
                <i class="fa fa-spinner fa-spin mr-1"></i>Đang tải…
              </div>
              <div v-else-if="preview">
                <div class="row small">
                  <div class="col-6">
                    Ngày chuẩn: <b>{{ preview.ngayChuanCty ?? "—" }}</b>
                  </div>
                  <div class="col-6">
                    Ngày thực tế: <b>{{ preview.ngayThucTe ?? "—" }}</b>
                  </div>
                  <div class="col-6">
                    Tổng thu nhập: <b>{{ money(preview.tongThuNhap) }}</b>
                  </div>
                  <div class="col-6">
                    Tổng khấu trừ:
                    <b class="text-danger">{{ money(preview.tongKhauTru) }}</b>
                  </div>
                  <div class="col-6">
                    Từ lệnh: <b>{{ money(preview.tongTuLenh) }}</b>
                  </div>
                  <div class="col-6">
                    Thực lĩnh:
                    <b class="text-success">{{ money(preview.thucLinh) }}</b>
                  </div>
                </div>
              </div>
              <div v-else class="text-muted">Không có dữ liệu.</div>
            </div>
          </div>
        </div>

        <!-- ===== CỘT PHẢI: Phiếu NV + Chi tiết ===== -->
        <div class="col-xl-7">
          <div class="card shadow-soft">
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between mb-2"
              >
                <h5 class="card-title mb-0">Phiếu lương nhân viên</h5>
                <div class="d-flex align-items-center">
                  <select
                    class="form-control form-control-sm mr-2"
                    v-model="ui.pbIdForUsers"
                    @change="fetchNguoiDungOptions"
                    style="min-width: 220px"
                  >
                    <option :value="null">— Lọc theo phòng ban —</option>
                    <option
                      v-for="pb in options.phongBans"
                      :key="'u' + (pb.idPhongBan || pb.id)"
                      :value="pb.idPhongBan || pb.id"
                    >
                      {{ pb.tenPhongBan || pb.ten }}
                    </option>
                  </select>
                  <input
                    class="form-control form-control-sm mr-2"
                    style="min-width: 220px"
                    v-model.trim="ui.userQ"
                    placeholder="Tìm nhân viên…"
                    @keydown.enter.prevent="fetchNguoiDungOptions"
                  />
                  <button
                    class="btn btn-outline-secondary btn-sm"
                    @click="fetchNguoiDungOptions"
                    :disabled="loading.users"
                  >
                    <i class="fa fa-search"></i>
                  </button>
                </div>
              </div>

              <div class="row">
                <div class="col-lg-5 border-right">
                  <div
                    class="list-group small"
                    style="max-height: 440px; overflow: auto"
                  >
                    <button
                      v-for="u in users"
                      :key="u.idNguoiDung || u.id"
                      type="button"
                      class="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                      :class="{
                        active:
                          cur.user &&
                          (cur.user.idNguoiDung || cur.user.id) ===
                            (u.idNguoiDung || u.id),
                      }"
                      @click="selectUser(u)"
                    >
                      <span class="ellipsis-1">{{
                        u.hoTen ||
                        u.tenDangNhap ||
                        "#" + (u.idNguoiDung || u.id)
                      }}</span>
                      <i class="fa fa-angle-right"></i>
                    </button>
                    <div v-if="users.length === 0" class="text-muted p-2">
                      Không có kết quả.
                    </div>
                  </div>
                </div>

                <div class="col-lg-7">
                  <div v-if="!bl.id" class="text-muted">
                    Hãy tạo bảng lương trước.
                  </div>
                  <div v-else-if="!cur.user" class="text-muted">
                    Chọn một nhân viên để xem phiếu.
                  </div>

                  <div v-else>
                    <!-- Summary header -->
                    <div
                      class="d-flex align-items-center justify-content-between"
                    >
                      <div>
                        <div class="font-weight-bold">
                          {{ cur.phieu?.hoTen || cur.user?.hoTen }}
                        </div>
                        <div class="small text-muted">
                          #{{ cur.phieu?.idBLNV || "—" }}
                        </div>
                      </div>
                      <div>
                        <button
                          class="btn btn-sm btn-outline-dark"
                          :disabled="!cur.phieu?.idBLNV || loading.exportOne"
                          @click="exportPhieu"
                        >
                          <span v-if="loading.exportOne"
                            ><i class="fa fa-spinner fa-spin mr-1"></i
                            >Xuất…</span
                          >
                          <span v-else
                            ><i class="fa fa-file-excel-o mr-1"></i>Export
                            phiếu</span
                          >
                        </button>
                      </div>
                    </div>

                    <div class="row small mt-2">
                      <div class="col-6">
                        Ngày chuẩn: <b>{{ cur.phieu?.ngayChuanCty ?? "—" }}</b>
                      </div>
                      <div class="col-6">
                        Ngày thực tế: <b>{{ cur.phieu?.ngayThucTe ?? "—" }}</b>
                      </div>
                      <div class="col-6">
                        Tổng thu nhập:
                        <b>{{ money(cur.phieu?.tongThuNhap) }}</b>
                      </div>
                      <div class="col-6">
                        Tổng khấu trừ:
                        <b class="text-danger">{{
                          money(cur.phieu?.tongKhauTru)
                        }}</b>
                      </div>
                      <div class="col-6">
                        Từ lệnh: <b>{{ money(cur.phieu?.tongTuLenh) }}</b>
                      </div>
                      <div class="col-6">
                        Thực lĩnh:
                        <b class="text-success">{{
                          money(cur.phieu?.thucLinh)
                        }}</b>
                      </div>
                    </div>

                    <hr />
                    <div
                      class="d-flex align-items-center justify-content-between mb-1"
                    >
                      <div class="font-weight-600">Chi tiết các khoản</div>
                      <div class="small text-muted">
                        Sửa = override tạm thời cho kỳ này
                      </div>
                    </div>

                    <!-- Bảng chi tiết -->
                    <div class="table-responsive" v-if="items.length">
                      <table
                        class="table table-sm table-bordered table-hover mb-2"
                      >
                        <thead class="thead-light">
                          <tr>
                            <th style="width: 34px">#</th>
                            <th>Khoản</th>
                            <th>Loại</th>
                            <th>Phương thức</th>
                            <th class="text-right" style="min-width: 120px">
                              Giá trị gốc
                            </th>
                            <th style="min-width: 120px">Override</th>
                            <th>Ghi chú</th>
                            <th style="width: 60px"></th>
                            <th style="width: 36px"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="(it, idx) in items"
                            :key="it.idBLCT || idx"
                          >
                            <td>{{ idx + 1 }}</td>
                            <td>
                              <div class="font-weight-600">
                                {{ it.tenKhoang || it.maKhoang || "—" }}
                              </div>
                              <div
                                class="small text-muted"
                                v-if="it.maKhoang && it.tenKhoang"
                              >
                                {{ it.maKhoang }}
                              </div>
                            </td>
                            <td>
                              <span
                                class="badge"
                                :class="badgeByLoai(it.loai)"
                                >{{ mapLoai(it.loai) }}</span
                              >
                            </td>
                            <td>
                              <span class="badge badge-light">{{
                                mapPhuongThuc(it.phuongThucTinh)
                              }}</span>
                            </td>
                            <td class="text-right">
                              {{ money(it.giaTriThang ?? it.soTien) }}
                            </td>
                            <td>
                              <input
                                type="number"
                                step="0.01"
                                class="form-control form-control-sm"
                                v-model.number="edit[itKey(it)].giaTriOverride"
                                :disabled="bl.locked || saving[itKey(it)]"
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                class="form-control form-control-sm"
                                v-model.trim="edit[itKey(it)].ghiChu"
                                :disabled="bl.locked || saving[itKey(it)]"
                              />
                            </td>
                            <td>
                              <button
                                class="btn btn-sm btn-success btn-block"
                                :disabled="bl.locked || saving[itKey(it)]"
                                @click="saveChiTiet(it)"
                              >
                                <span v-if="saving[itKey(it)]"
                                  ><i class="fa fa-spinner fa-spin"></i
                                ></span>
                                <span v-else><i class="fa fa-save"></i></span>
                              </button>
                            </td>
                            <td>
                              <button
                                class="btn btn-sm btn-outline-secondary"
                                :disabled="bl.locked || saving[itKey(it)]"
                                @click="openSaveToConfig(it)"
                              >
                                <i class="fa fa-bookmark-o"></i>
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-else class="text-muted">Không có chi tiết.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal: Lưu thành cấu hình nhân viên -->
      <div
        v-if="mod.saveCfg.open"
        class="modal custom-modal fade show"
        style="display: block"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header border-0">
              <h5 class="modal-title">Lưu khoản thành cấu hình NV</h5>
              <button type="button" class="close" @click="closeSaveToConfig">
                <span>&times;</span>
              </button>
            </div>
            <div class="modal-body">
              <div class="small text-muted mb-2">
                Khoản:
                <b>{{
                  mod.saveCfg.item?.tenKhoang || mod.saveCfg.item?.maKhoang
                }}</b>
              </div>
              <div class="form-group">
                <label class="small">Hiệu lực từ (YYYY-MM)</label>
                <input
                  type="month"
                  class="form-control"
                  v-model="mod.saveCfg.hieuLucTu"
                />
              </div>
              <div class="form-group">
                <label class="small">Hiệu lực đến (tuỳ chọn)</label>
                <input
                  type="month"
                  class="form-control"
                  v-model="mod.saveCfg.hieuLucDen"
                />
              </div>
            </div>
            <div class="modal-footer">
              <button
                class="btn btn-outline-secondary"
                @click="closeSaveToConfig"
              >
                Hủy
              </button>
              <button
                class="btn btn-success"
                :disabled="saving.saveCfg || !mod.saveCfg.hieuLucTu"
                @click="saveToConfig"
              >
                <span v-if="saving.saveCfg"
                  ><i class="fa fa-spinner fa-spin mr-1"></i>Đang lưu…</span
                >
                <span v-else><i class="fa fa-check mr-1"></i>Lưu</span>
              </button>
            </div>
          </div>
        </div>
        <div class="modal-backdrop fade show"></div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import axios from "axios";

/** Tạo axios ngay trong file .vue như bạn yêu cầu */
const api = axios.create({
  baseURL: process.env.VUE_APP_BASEURL,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});
let isRefreshing = false;
let refreshWaitQueue = [];
function onRefreshed(newAccessToken) {
  refreshWaitQueue.forEach((cb) => cb(newAccessToken));
  refreshWaitQueue = [];
}
function addToQueue(cb) {
  refreshWaitQueue.push(cb);
}
api.interceptors.request.use((config) => {
  if (!config.url.includes("/auth/refresh")) {
    const token = localStorage.getItem("accessToken");
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config || {};
    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry
    ) {
      if (isRefreshing) {
        return new Promise((resolve) =>
          addToQueue((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            resolve(api(originalRequest));
          })
        );
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        const { data } = await axios.post(
          process.env.VUE_APP_BASEURL + "/api/auth/refresh",
          { refreshToken }
        );
        const newToken = data?.data?.accessToken || data?.accessToken;
        localStorage.setItem("accessToken", newToken);
        isRefreshing = false;
        onRefreshed(newToken);
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (e) {
        isRefreshing = false;
        return Promise.reject(e);
      }
    }
    return Promise.reject(error);
  }
);

export default {
  name: "PayrollManager",
  data() {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0"
    )}`;
    return {
      ui: { month: ym, pbId: null, pbIdForUsers: null, userQ: "" },
      form: { moTa: "" },
      bl: {
        id: null,
        thang: null,
        nam: null,
        moTa: null,
        trangThai: null,
        ngayChot: null,
        locked: false,
      },
      options: { phongBans: [] },
      users: [],
      cur: { user: null, phieu: null },
      items: [],
      edit: {},
      preview: null,
      last: { lapCount: null, calcCount: null },
      loading: {
        any: false,
        taobang: false,
        pbs: false,
        users: false,
        lapnv: false,
        tinh: false,
        khoa: false,
        exportAll: false,
        exportOne: false,
        preview: false,
      },
      saving: {},
      mod: {
        saveCfg: { open: false, item: null, hieuLucTu: null, hieuLucDen: null },
      },
    };
  },
  mounted() {
    this.fetchPhongBanOptions();
    this.fetchPreview();
  },
  methods: {
    /* ===== Utils ===== */
    money(v) {
      const n = Number(v || 0);
      return n.toLocaleString("vi-VN") + " đ";
    },
    itKey(it) {
      return String(it.idBLCT || it.id || it.maKhoang || it.tenKhoang);
    },
    badgeByLoai(loai) {
      const x = (loai || "").toUpperCase();
      return {
        "badge-primary": x === "LUONG_CO_BAN",
        "badge-success": x === "PHU_CAP",
        "badge-danger": x === "KHAU_TRU",
      };
    },
    mapLoai(loai) {
      const x = (loai || "").toUpperCase();
      if (x === "PHU_CAP") return "Phụ cấp";
      if (x === "KHAU_TRU") return "Khấu trừ";
      if (x === "LUONG_CO_BAN") return "Lương cơ bản";
      return x || "—";
    },
    // Hỗ trợ cả THEO_NGAY & CHIA_NGAY_THUC_TE
    mapPhuongThuc(pt) {
      const x = (pt || "").toUpperCase();
      if (x === "CO_DINH") return "Cố định";
      if (x === "THEO_NGAY" || x === "CHIA_NGAY_THUC_TE")
        return "Theo ngày làm thực tế";
      return x || "—";
    },
    unwrap(r) {
      return r?.data?.data ?? r?.data ?? r;
    },
    async alertOk(title = "Thành công", text = "Đã lưu") {
      await Swal.fire({
        icon: "success",
        title,
        text,
        timer: 1200,
        showConfirmButton: false,
      });
    },
    async alertErr(e, title = "Thao tác thất bại") {
      const msg = e?.response?.data?.message || e?.message || "Có lỗi xảy ra";
      await Swal.fire({ icon: "error", title, text: msg });
    },

    onMonthChange() {
      this.fetchPreview();
    },
    async refreshAll() {
      try {
        this.loading.any = true;
        if (this.cur.user && this.bl.id) {
          await this.loadPhieuFor(this.cur.user);
        }
        await this.fetchPreview();
      } finally {
        this.loading.any = false;
      }
    },

    /* ===== B1: Tạo bảng lương ===== */
    async taoBang() {
      try {
        this.loading.taobang = true;
        const [y, m] = this.ui.month.split("-").map(Number);
        const { data } = await api.post("/api/bang-luong/tao", {
          thang: m,
          nam: y,
          moTa: this.form.moTa || `Lương ${this.ui.month}`,
        });
        const res = this.unwrap(data);
        this.bl = {
          id: res?.id,
          thang: res?.thang,
          nam: res?.nam,
          moTa: res?.moTa,
          trangThai: res?.trangThai,
          ngayChot: res?.ngayChot,
          locked: false,
        };
        await this.alertOk("Đã tạo bảng lương", `Tháng ${m}/${y}`);
      } catch (e) {
        await this.alertErr(e);
      } finally {
        this.loading.taobang = false;
      }
    },

    /* ===== B2: Lập NV ===== */
    async fetchPhongBanOptions() {
      try {
        this.loading.pbs = true;
        const r = await api.get("/api/phong-ban/options");
        const list = this.unwrap(r) || [];
        this.options.phongBans = Array.isArray(list)
          ? list
          : list?.content || [];
      } catch {
        this.options.phongBans = [];
      } finally {
        this.loading.pbs = false;
      }
    },
    async lapNhanVien() {
      if (!this.bl.id) return;
      try {
        this.loading.lapnv = true;
        const body = this.ui.pbId ? { idPhongBan: this.ui.pbId } : {};
        const r = await api.post(
          `/api/bang-luong/${this.bl.id}/lap-nhan-vien`,
          Object.keys(body).length ? body : null
        );
        this.last.lapCount = this.unwrap(r) ?? 0;
        await this.alertOk(
          "Đã lập danh sách",
          `${this.last.lapCount} nhân viên`
        );
      } catch (e) {
        await this.alertErr(e);
      } finally {
        this.loading.lapnv = false;
      }
    },

    /* ===== B3: Tính toán ===== */
    async tinhToan() {
      if (!this.bl.id) return;
      try {
        this.loading.tinh = true;
        const r = await api.post(`/api/bang-luong/${this.bl.id}/tinh`);
        this.last.calcCount = this.unwrap(r) ?? null;
        await this.alertOk(
          "Đã tính xong",
          `${this.last.calcCount ?? ""} nhân viên`
        );
        if (this.cur.user) {
          await this.loadPhieuFor(this.cur.user);
        }
      } catch (e) {
        await this.alertErr(e);
      } finally {
        this.loading.tinh = false;
      }
    },

    /* ===== B4: Khoá & Export ===== */
    async khoaBang() {
      if (!this.bl.id) return;
      try {
        this.loading.khoa = true;
        await api.post(`/api/bang-luong/${this.bl.id}/khoa`);
        this.bl.locked = true;
        await this.alertOk("Đã khoá bảng lương");
      } catch (e) {
        await this.alertErr(e);
      } finally {
        this.loading.khoa = false;
      }
    },
    async exportBangLuong() {
      try {
        this.loading.exportAll = true;
        const r = await api.get(`/api/bang-luong/${this.bl.id}/export-excel`, {
          responseType: "blob",
        });
        this.downloadBlob(r, `bang-luong_${this.bl.id}.xlsx`);
      } catch (e) {
        await this.alertErr(e, "Xuất Excel thất bại");
      } finally {
        this.loading.exportAll = false;
      }
    },

    /* ===== Users & Phiếu ===== */
    async fetchNguoiDungOptions() {
      try {
        this.loading.users = true;
        const params = {
          q: this.ui.userQ || undefined,
          limit: 200,
          phongBanId: this.ui.pbIdForUsers || undefined,
        };
        const r = await api.get("/api/nguoi-dung/options", { params });
        const list = this.unwrap(r) || [];
        this.users = Array.isArray(list) ? list : list?.content || [];
      } catch {
        this.users = [];
      } finally {
        this.loading.users = false;
      }
    },
    async selectUser(u) {
      this.cur.user = u;
      this.cur.phieu = null;
      this.items = [];
      this.edit = {};
      this.saving = {};
      if (this.bl.id) await this.loadPhieuFor(u);
    },
    async loadPhieuFor(u) {
      try {
        const idU = u.idNguoiDung || u.id;
        const r = await api.get(
          `/api/bang-luong/${this.bl.id}/nhan-vien/${idU}`
        );
        const phieu = this.unwrap(r);
        this.cur.phieu = phieu;
        await this.fetchChiTiet(phieu.idBLNV);
      } catch (e) {
        await this.alertErr(e, "Không tải được phiếu");
      }
    },
    async fetchChiTiet(idBLNV) {
      try {
        const r = await api.get(`/api/bang-luong/nhan-vien/${idBLNV}/chi-tiet`);
        const detail = this.unwrap(r);
        const arr = Array.isArray(detail?.items)
          ? detail.items
          : Array.isArray(detail)
          ? detail
          : [];
        this.items = arr;
        const m = {};
        arr.forEach((it) => {
          m[this.itKey(it)] = {
            giaTriOverride: it.giaTriOverride ?? null,
            ghiChu: it.ghiChu ?? "",
          };
        });
        this.edit = m;
      } catch (e) {
        this.items = [];
        await this.alertErr(e, "Không tải được chi tiết");
      }
    },
    async saveChiTiet(it) {
      try {
        const key = this.itKey(it);
        this.$set(this.saving, key, true);
        const body = {
          giaTriOverride: this.edit[key]?.giaTriOverride ?? null,
          ghiChu: this.edit[key]?.ghiChu ?? "",
        };
        await api.patch(
          `/api/bang-luong/nhan-vien/chi-tiet/${it.idBLCT}`,
          body
        );
        await this.alertOk();
        if (this.cur.user) await this.loadPhieuFor(this.cur.user);
      } catch (e) {
        await this.alertErr(e);
      } finally {
        const key = this.itKey(it);
        this.$set(this.saving, key, false);
      }
    },
    openSaveToConfig(it) {
      const [y, m] = this.ui.month.split("-");
      this.mod.saveCfg = {
        open: true,
        item: it,
        hieuLucTu: `${y}-${m}`,
        hieuLucDen: null,
      };
    },
    closeSaveToConfig() {
      this.mod.saveCfg.open = false;
    },
    async saveToConfig() {
      try {
        if (!this.mod.saveCfg.hieuLucTu) {
          await this.alertErr(null, 'Thiếu "Hiệu lực từ"');
          return;
        }
        this.saving.saveCfg = true;
        const { item, hieuLucTu, hieuLucDen } = this.mod.saveCfg;
        await api.post(
          `/api/bang-luong/nhan-vien/chi-tiet/${item.idBLCT}/luu-thanh-cau-hinh`,
          { hieuLucTu, hieuLucDen }
        );
        await this.alertOk("Đã lưu thành cấu hình");
        this.closeSaveToConfig();
      } catch (e) {
        await this.alertErr(e);
      } finally {
        this.saving.saveCfg = false;
      }
    },
    async exportPhieu() {
      try {
        this.loading.exportOne = true;
        const r = await api.get(
          `/api/bang-luong/nhan-vien/${this.cur.phieu.idBLNV}/export-excel`,
          { responseType: "blob" }
        );
        this.downloadBlob(r, `phieu-luong_${this.cur.phieu.idBLNV}.xlsx`);
      } catch (e) {
        await this.alertErr(e, "Xuất phiếu thất bại");
      } finally {
        this.loading.exportOne = false;
      }
    },

    /* ===== My Preview ===== */
    async fetchPreview() {
      try {
        this.loading.preview = true;
        const r = await api.get("/api/payroll/my/preview", {
          params: { month: this.ui.month },
        });
        this.preview = this.unwrap(r);
      } catch {
        this.preview = null;
      } finally {
        this.loading.preview = false;
      }
    },

    /* ===== Download Helper ===== */
    downloadBlob(response, fallbackName) {
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/octet-stream",
      });
      let filename = fallbackName;
      try {
        const disp =
          response.headers["content-disposition"] ||
          response.headers["Content-Disposition"];
        if (disp) {
          const m = /filename="?([^";]+)"?/i.exec(disp);
          if (m && m[1]) filename = decodeURIComponent(m[1]);
        }
      } catch {}
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    },
  },
};
</script>

<style scoped>
.shadow-soft {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
}
.border-left-primary {
  border-left: 4px solid #0d6efd22;
}
.border-left-info {
  border-left: 4px solid #17a2b833;
}
.border-left-warning {
  border-left: 4px solid #ffc10733;
}
.border-left-success {
  border-left: 4px solid #28a74533;
}
.bg-disabled {
  opacity: 0.65;
  pointer-events: none;
}
.ellipsis-1 {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.font-weight-600 {
  font-weight: 600;
}
.table td,
.table th {
  vertical-align: middle;
}
</style>
